/**
 * CompositeTimeline - Visual timeline showing all clips with transitions
 * 
 * Displays all video clips on a single timeline with:
 * - Draggable playhead for scrubbing
 * - Visual clip blocks with thumbnails
 * - Transition indicators between clips
 * - Waveform visualization (placeholder)
 */

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Scissors, Volume2 } from 'lucide-react';
import { TRANSITION_TYPES } from '../../services/TransitionManager';
import './CompositeTimeline.css';

export const CompositeTimeline = ({
  clips,
  currentTime,
  totalDuration,
  transitionDuration = 0.5,
  transitionType = 'fade',
  onSeek,
  onClipSelect,
  onTransitionChange,
  selectedClipId,
  audioTrack,
}) => {
  const timelineRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showTransitionMenu, setShowTransitionMenu] = useState(null);
  
  // Calculate clip positions on timeline
  const getClipPositions = useCallback(() => {
    if (!clips || clips.length === 0) return [];
    
    let currentPosition = 0;
    const positions = [];
    
    clips.forEach((clip, index) => {
      const clipDuration = (clip.endTime || 5) - (clip.startTime || 0);
      
      positions.push({
        ...clip,
        index,
        timelineStart: currentPosition,
        timelineEnd: currentPosition + clipDuration,
        clipDuration,
      });
      
      // Account for transition overlap (except for last clip)
      if (index < clips.length - 1) {
        currentPosition += clipDuration - transitionDuration;
      } else {
        currentPosition += clipDuration;
      }
    });
    
    return positions;
  }, [clips, transitionDuration]);
  
  const clipPositions = getClipPositions();
  
  // Handle timeline click/drag for seeking
  const handleTimelineInteraction = useCallback((e) => {
    if (!timelineRef.current || totalDuration <= 0) return;
    
    const rect = timelineRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const progress = x / rect.width;
    const time = progress * totalDuration;
    
    if (onSeek) {
      onSeek(time);
    }
  }, [totalDuration, onSeek]);
  
  const handleMouseDown = (e) => {
    setIsDragging(true);
    handleTimelineInteraction(e);
  };
  
  
  useEffect(() => {
    const moveHandler = (e) => {
      if (isDragging) {
        handleTimelineInteraction(e);
      }
    };
    const upHandler = () => setIsDragging(false);
    
    if (isDragging) {
      window.addEventListener('mousemove', moveHandler);
      window.addEventListener('mouseup', upHandler);
    }
    
    return () => {
      window.removeEventListener('mousemove', moveHandler);
      window.removeEventListener('mouseup', upHandler);
    };
  }, [isDragging, handleTimelineInteraction]);
  
  // Format time as MM:SS.m
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 10);
    return `${mins}:${secs.toString().padStart(2, '0')}.${ms}`;
  };
  
  // Generate time markers
  const generateTimeMarkers = () => {
    if (totalDuration <= 0) return [];
    
    const markers = [];
    const interval = totalDuration > 30 ? 5 : totalDuration > 10 ? 2 : 1;
    
    for (let t = 0; t <= totalDuration; t += interval) {
      markers.push({
        time: t,
        position: (t / totalDuration) * 100,
      });
    }
    
    return markers;
  };
  
  const timeMarkers = generateTimeMarkers();
  
  // Handle transition type change
  const handleTransitionSelect = (type) => {
    if (onTransitionChange) {
      onTransitionChange(type);
    }
    setShowTransitionMenu(null);
  };
  
  return (
    <div className="composite-timeline">
      {/* Time ruler */}
      <div className="time-ruler">
        {timeMarkers.map((marker, i) => (
          <div 
            key={i}
            className="time-marker"
            style={{ left: `${marker.position}%` }}
          >
            <div className="marker-tick" />
            <span className="marker-label">{formatTime(marker.time)}</span>
          </div>
        ))}
      </div>
      
      {/* Main timeline track */}
      <div 
        className="timeline-track"
        ref={timelineRef}
        onMouseDown={handleMouseDown}
      >
        {/* Clip blocks */}
        <div className="clips-container">
          {clipPositions.map((clip, index) => {
            const startPercent = Math.max(0, (clip.timelineStart / totalDuration) * 100);
            // Ensure clip doesn't extend past 100%
            const maxWidth = 100 - startPercent;
            const widthPercent = Math.min((clip.clipDuration / totalDuration) * 100, maxWidth);
            const isSelected = selectedClipId === clip.id;
            const isActive = currentTime >= clip.timelineStart && currentTime < clip.timelineEnd;
            
            return (
              <React.Fragment key={clip.id}>
                {/* Clip block */}
                <div
                  className={`clip-block ${isSelected ? 'selected' : ''} ${isActive ? 'active' : ''}`}
                  style={{
                    left: `${startPercent}%`,
                    width: `${widthPercent}%`,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onClipSelect) onClipSelect(clip.id);
                  }}
                >
                  {/* Thumbnail preview */}
                  <div className="clip-thumbnail">
                    {clip.thumbnail ? (
                      <img src={clip.thumbnail} alt={clip.label} />
                    ) : (
                      <div className="thumbnail-placeholder">
                        <span className="clip-number">{index + 1}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Clip info */}
                  <div className="clip-info">
                    <span className="clip-label">{clip.label || `Clip ${index + 1}`}</span>
                    <span className="clip-duration">{clip.clipDuration.toFixed(1)}s</span>
                  </div>
                  
                  {/* Trim handles */}
                  <div className="trim-handle trim-handle-left">
                    <Scissors size={12} />
                  </div>
                  <div className="trim-handle trim-handle-right">
                    <Scissors size={12} />
                  </div>
                </div>
                
                {/* Transition indicator (between clips) */}
                {index < clipPositions.length - 1 && (
                  <div
                    className="transition-indicator"
                    style={{
                      left: `${((clip.timelineEnd - transitionDuration) / totalDuration) * 100}%`,
                      width: `${(transitionDuration / totalDuration) * 100}%`,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowTransitionMenu(showTransitionMenu === index ? null : index);
                    }}
                  >
                    <div className="transition-icon">
                      <span className="transition-type">{TRANSITION_TYPES[transitionType]?.name || 'Fade'}</span>
                    </div>
                    
                    {/* Transition menu */}
                    {showTransitionMenu === index && (
                      <div className="transition-menu">
                        <div className="transition-menu-header">Select Transition</div>
                        <div className="transition-options">
                          {Object.entries(TRANSITION_TYPES).slice(0, 8).map(([key, value]) => (
                            <button
                              key={key}
                              className={`transition-option ${transitionType === key ? 'active' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTransitionSelect(key);
                              }}
                            >
                              {value.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
        
        {/* Playhead */}
        <div 
          className="playhead"
          style={{ left: `${totalDuration > 0 ? (currentTime / totalDuration) * 100 : 0}%` }}
        >
          <div className="playhead-head" />
          <div className="playhead-line" />
        </div>
      </div>
      
      {/* Audio track (if present) */}
      {audioTrack && (
        <div className="audio-track">
          <div className="track-label">
            <Volume2 size={14} />
            <span>Audio</span>
          </div>
          <div className="audio-waveform">
            <div className="waveform-placeholder">
              {audioTrack.name || 'Background Music'}
            </div>
          </div>
        </div>
      )}
      
      {/* Current time indicator */}
      <div className="current-time-display">
        {formatTime(currentTime)} / {formatTime(totalDuration)}
      </div>
    </div>
  );
};

export default CompositeTimeline;

